import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { CANONICAL_ORIGIN } from "@/lib/seo";

const PAGES = {
  about: {
    path: "/om-spoergmig",
    label: "Om SpørgMig",
    title: "Om SpørgMig | Personlig hjælp fra rigtige mennesker",
    description: "Læs om SpørgMig og hvordan platformen forbinder mennesker med godkendte eksperter til personlig hjælp via chat, billeder og video.",
    heading: "Personlig hjælp fra mennesker med erfaring",
    intro: "SpørgMig er en dansk markedsplads for menneskelig viden. Vi gør det nemmere at finde en relevant ekspert når et konkret problem er svært at løse alene.",
    sections: [
      ["Hvorfor SpørgMig findes", "Mange problemer er for konkrete til en standardsøgning og for små til et langt konsulentforløb. På SpørgMig kan kunden beskrive sin situation, sammenligne relevante eksperter og aftale pris og omfang før hjælpen begynder."],
      ["Hvordan platformen fungerer", "SpørgMig samler selvstændige eksperter på én platform. Hjælpen kan foregå gennem chat, billeder eller video. Eksperten leverer sin tid og faglige vurdering. SpørgMig står for platformen, overblikket og den digitale proces."],
      ["Åbenhed før valg", "Ekspertprofiler viser blandt andet erfaring, specialer, priser og anmeldelser når de findes. Målet er at give kunden et ordentligt grundlag for at vælge den person der passer til opgaven."],
    ],
  },
  verification: {
    path: "/ekspertgodkendelse",
    label: "Sådan godkendes profiler",
    title: "Sådan godkendes profiler | SpørgMig",
    description: "Se hvordan ekspertprofiler bliver gennemgået før de vises offentligt på SpørgMig og hvad en verificering betyder.",
    heading: "Sådan gennemgår vi ekspertprofiler",
    intro: "En ekspertprofil bliver ikke offentliggjort automatisk. Den skal først udfyldes og sendes til manuel gennemgang.",
    sections: [
      ["Profilen skal være fyldestgørende", "Eksperten skal oplyse navn, fagområde, erfaring, specialer, sprog, priser og en beskrivelse af sin baggrund. Profilbilledet skal vise personen tydeligt."],
      ["Godkendelse og verificering er ikke det samme", "Godkendt betyder at profilen er gennemgået og må vises på platformen. Dokumenterede uddannelser, certifikater eller virksomhedsforhold vises kun som verificerede når SpørgMig har gennemgået den relevante dokumentation."],
      ["Løbende kvalitet", "Anmeldelser knyttes til gennemførte opgaver når funktionen er relevant. Profiler kan skjules eller gennemgås igen hvis oplysninger virker misvisende eller platformens regler ikke bliver fulgt."],
    ],
  },
  safety: {
    path: "/sikkerhed",
    label: "Sikkerhed",
    title: "Tryg og tydelig hjælp online | SpørgMig",
    description: "Læs hvordan SpørgMig arbejder med tydelige aftaler, godkendte ekspertprofiler og beskyttelse af private oplysninger.",
    heading: "Tryghed begynder med tydelige rammer",
    intro: "SpørgMig er bygget til at gøre det klart hvem kunden taler med, hvad hjælpen omfatter og hvad den koster før en aftale bliver aktiv.",
    sections: [
      ["Aftal pris og omfang først", "Kunden kan se ekspertens profil og modtage et konkret forslag før valget træffes. Betaling gælder ekspertens tid og faglige vurdering. Et bestemt resultat kan ikke garanteres."],
      ["Hold dialogen på platformen", "Chat, billeder og oplysninger om opgaven bør deles gennem SpørgMig. Det giver begge parter et samlet forløb og gør det lettere at forstå hvad der er aftalt."],
      ["Del kun det nødvendige", "Undlad at sende adgangskoder, betalingskortoplysninger, CPR-numre eller andre følsomme oplysninger som eksperten ikke behøver for at hjælpe. Ved akut fare eller spørgsmål der kræver myndigheds- eller sundhedsberedskab skal de relevante officielle kanaler bruges."],
    ],
  },
};

export default function TrustPage({ page }) {
  const data = PAGES[page];
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Forside", item: `${CANONICAL_ORIGIN}/` },
      { "@type": "ListItem", position: 2, name: data.label, item: `${CANONICAL_ORIGIN}${data.path}` },
    ],
  };

  return <>
    <Seo title={data.title} description={data.description} path={data.path} schemas={schema}/>
    <main className="mx-auto max-w-4xl px-4 py-10 sm:px-8 sm:py-16">
      <p className="font-mono text-xs uppercase tracking-widest text-brand-fresh">{data.label}</p>
      <h1 className="mt-3 max-w-3xl text-3xl font-bold leading-tight tracking-tight text-brand-ink sm:text-5xl">{data.heading}</h1>
      <p className="mt-5 max-w-3xl text-base leading-7 text-brand-body sm:text-lg">{data.intro}</p>
      <div className="mt-10 space-y-4">
        {data.sections.map(([heading, body]) => (
          <section key={heading} className="rounded-2xl border border-brand-line bg-white p-6 sm:p-8">
            <h2 className="text-xl font-semibold text-brand-ink">{heading}</h2>
            <p className="mt-3 leading-7 text-brand-body">{body}</p>
          </section>
        ))}
      </div>
      <section className="mt-10 rounded-2xl bg-brand-soft p-6 sm:p-8">
        <h2 className="text-xl font-semibold text-brand-ink">Har du brug for hjælp?</h2>
        <p className="mt-2 text-sm leading-6 text-brand-body">Se godkendte profiler og find en ekspert der passer til dit konkrete problem.</p>
        <Link to="/eksperter" className="btn-primary mt-4 inline-flex">Find en ekspert</Link>
      </section>
    </main>
  </>;
}

